'use client';

import { useGetEntitlements } from '@/entitlements/api/get';
import { useAnalyticsPageView } from '@/hooks/use-analytics-page-view';
import { useAuthStore } from '@/stores/auth-store-provider';
import { DeepNullable, Nullable, User, UserInfoAddress } from '@/types';
import { isDevEnv } from '@/utils/env';
import { safeJsonParse } from '@/utils/safe-json';
import { useEffect } from 'react';
import type { RudderAnalytics } from '@rudderstack/analytics-js';
import isEmpty from 'lodash-es/isEmpty';
import omitBy from 'lodash-es/omitBy';
import pickBy from 'lodash-es/pickBy';
import isEqual from 'lodash-es/isEqual';
import { useStatsigClient } from '@statsig/react-bindings';

declare global {
  let rudderanalytics: RudderAnalytics & {
    proxied: boolean
  };
}

let isRudderStackReady = false;

function findObjectDifference(
  objectA: Record<string, any>,
  objectB: Nullable<Record<string, any>> | undefined
) {
  return pickBy(objectA, (valueA, keyA) => !isEqual(valueA, objectB?.[keyA]));
}

export type RudderanalyticsIdentifyInput = DeepNullable<Partial<User>> & {
  adjustWebUUID?: string | null;
  'Sign Up Date'?: string | null;
  entitlements?: string[] | null;
};

export function rudderanalyticsIdentify(
  input: RudderanalyticsIdentifyInput = {}
) {
  if (
    !isRudderStackReady ||
    !rudderanalytics.getUserId ||
    !rudderanalytics.getUserTraits
  )
    return;

  const rudderUserId = rudderanalytics.getUserId();
  const rudderUserTraits = rudderanalytics.getUserTraits();

  // // Custom uuid for existing 2.0 users
  // // For newly created users, custom uuid and id is same
  // // After signup, custom:uuid is set in background thereby takes time and
  // // comes up null just after signup
  const userId = input['custom:uuid'] || input.id;

  const address = safeJsonParse<UserInfoAddress>(input?.address?.formatted);

  const inputTraits = {
    userId,
    firstName: input.given_name,
    lastName: input.family_name,
    name: input.name,
    email: input.email,
    phone: input.phone_number,
    country: address?.country,
    state: address?.state,
    entitlements: input.entitlements,
    Timezone: input.zoneinfo,
    adjustWebUUID: input.adjustWebUUID,
    'Sign Up Date': input['Sign Up Date']
  };

  const userTraits = omitBy(inputTraits, isEmpty);
  const changedTraits = findObjectDifference(userTraits, rudderUserTraits);

  const shouldIdentify =
    (userId && rudderUserId !== userId) || !isEmpty(changedTraits);

  if (shouldIdentify) {
    if (userId) {
      rudderanalytics.identify(userId, userTraits);
    } else {
      rudderanalytics.identify(userTraits);
    }
  }
}

export function InitializeRudderstack() {
  useAnalyticsPageView();
  const authStore = useAuthStore();
  const { data: userEntitlements, isLoading } = useGetEntitlements();
  const user = authStore.useTracked.user();
  const stableID = useStatsigClient().client.getContext().stableID;

  useEffect(() => {
    if (userEntitlements && !isLoading) {
      rudderanalyticsIdentify({ entitlements: userEntitlements });
    }
  }, [userEntitlements, isLoading]);

  useEffect(() => {
    if (user) {
      rudderanalyticsIdentify(user);
    }
  }, [user]);

  useEffect(() => {
    if (!rudderanalytics.proxied) {
      let analytics = rudderanalytics;

      if (Array.isArray(rudderanalytics)) {
        rudderanalytics.forEach((args, index) => {
          if (args[0] === 'track') {
            const properties = args[2];
            const email = authStore.get.user()?.email;
            properties.email ||= email;
            properties.statsigCustomIDs ||= ['stableID', stableID];

            if (isDevEnv) {
              console.group('Rudderstack track event queued');
              console.log(...args.slice(1));
              console.groupEnd();
            }
          }
        });
      }
      window.rudderanalytics = new Proxy(analytics, {
        get(target, prop, receiver) {
          if (prop === 'track') {
            return (...args: any[]) => {
              const email = args?.[1]?.email || authStore.get.user()?.email;
              // Inject the statsig stableID into any existing event properties
              const properties = Object.assign(args?.[1] ?? {}, {
                statsigCustomIDs: ['stableID', stableID],
                email
              });
              args.splice(1, 1, properties);
              if (isDevEnv) {
                console.group('Rudderstack track event');
                console.log(...args);
                console.groupEnd();
              }
              Reflect.apply(target[prop], target, args);
            };
          }
          return Reflect.get(target, prop, receiver);
        }
      });
      rudderanalytics.proxied = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore.get, stableID, window.rudderanalytics]);

  useEffect(() => {
    rudderanalytics.ready(() => {
      isRudderStackReady = true;
      rudderanalyticsIdentify({ ...user, entitlements: userEntitlements });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render nothing - this component is only included so that the init code
  // above will run client-side
  return null;
}
